import React, { useState, useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTransitionState } from "react-transition-state";
import classNames from "classnames";

import * as Icons from "../icons";
import Button from "./Button";
import AsyncButton from "./AsyncButton";

const ESCAPE_KEYCODE = 27;

type Props = {
  readonly title?: ReactNode;
  readonly open?: boolean;
  readonly onCancel?: (e?: React.MouseEvent<HTMLDivElement>) => void;
  readonly onClose?: () => void;
  readonly onSave?: () => void | Promise<void>;
  readonly onOpen?: () => void;
  readonly onDelete?: React.MouseEventHandler<HTMLButtonElement>;
  readonly centered?: boolean;
  readonly includeCancelButton?: boolean;
  readonly includeSaveButton?: boolean;
  readonly includeTopRightClose?: boolean;
  readonly includeDeleteButton?: boolean;
  readonly warn?: boolean;
  readonly disableCancel?: boolean;
  readonly disableSave?: boolean;
  readonly disableDelete?: boolean;
  readonly backdropCancels?: boolean;
  readonly buttons?: ReactNode;
  readonly keyboard?: boolean;
  readonly children?: ReactNode;
  readonly saveButtonTextOverride?: string | React.ReactElement<any>;
  readonly deleteButtonTextOverride?: string;
  readonly cancelButtonTextOverride?: string;
  readonly errorState?: boolean;
  readonly className?: string;
  readonly minWidth?: number | string;
  readonly closeOnSave?: boolean;
  readonly closeOnDelete?: boolean;
  readonly backpackModal?: boolean;
  readonly ["data-cy"]?: string;
};

type ModalProps = Omit<Props, "onOpen"> & {
  readonly state:
    | "preEnter"
    | "entering"
    | "entered"
    | "preExit"
    | "exiting"
    | "exited"
    | "unmounted";
  readonly _setOpen: any;
};

const UnwrappedModal = (props: ModalProps) => {
  const {
    title,
    open,
    onCancel,
    onClose,
    onSave,
    onDelete,
    state,
    centered = false,
    keyboard = true,
    includeCancelButton = true,
    includeSaveButton = !!onSave,
    includeDeleteButton = false,
    includeTopRightClose = false,
    warn = false,
    disableCancel = false,
    disableSave = false,
    disableDelete = false,
    backdropCancels = true,
    children,
    saveButtonTextOverride,
    deleteButtonTextOverride,
    cancelButtonTextOverride,
    errorState = false,
    className,
    minWidth,
    backpackModal,
    buttons: buttonsProp,
    _setOpen,
    "data-cy": dataCy,
  } = props;

  const buttons = (
    <div
      className={classNames({
        "bractlet-modal--buttons": true,
        "--centered": centered,
        "--spread": !centered,
        "--backpack": backpackModal,
      })}
    >
      <div
        className={classNames({
          "bractlet-modal--save-buttons": includeSaveButton,
          "bractlet-modal--delete-buttons": includeDeleteButton,
        })}
      >
        {includeCancelButton && (
          <Button
            outline
            size={backpackModal ? "large" : "default"}
            color="gray"
            className={classNames("bractlet-modal--cancel-button", {
              "backpack-modal--cancel-button w-40": backpackModal,
            })}
            onClick={() => {
              _setOpen(false);
              if (onCancel) onCancel();
            }}
            disabled={disableCancel}
          >
            {cancelButtonTextOverride ? cancelButtonTextOverride : "Cancel"}
          </Button>
        )}
        {onSave && includeSaveButton && (
          <AsyncButton
            data-cy="modal-save-button"
            data-testid="modal-save-button"
            color="green"
            size={backpackModal ? "large" : "default"}
            className={classNames({
              "w-36": backpackModal,
            })}
            onClick={async () => {
              await onSave();
              if (props.closeOnSave) _setOpen(false);
            }}
            disabled={disableSave}
          >
            {saveButtonTextOverride ? saveButtonTextOverride : "Save"}
          </AsyncButton>
        )}
        {onDelete && includeDeleteButton && (
          <Button
            data-cy="modal-delete-button"
            data-testid="modal-delete-button"
            className="bractlet-modal--delete-button"
            onClick={(e) => {
              onDelete(e);
              if (props.closeOnDelete) _setOpen(false);
              e.stopPropagation();
            }}
            disabled={disableDelete}
            color={warn ? "yellow" : "red"}
          >
            {deleteButtonTextOverride || (warn ? "Deactivate" : "Delete")}
          </Button>
        )}
      </div>
      {buttonsProp !== undefined &&
        typeof buttonsProp !== "boolean" &&
        buttonsProp}
    </div>
  );

  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (keyboard && onCancel && backdropCancels) {
      const listener = (e: any) => {
        if (e.keyCode === ESCAPE_KEYCODE) onCancel();
      };

      window.addEventListener("keyup", listener);

      return () => window.removeEventListener("keyup", listener);
    }
  }, [keyboard, onCancel, backdropCancels]);

  useEffect(() => {
    if (!hasOpened && state !== "exited") {
      setHasOpened(true);
    } else if (onClose && hasOpened && !open && state === "exited") {
      onClose();
      setHasOpened(false);
    }
  }, [onClose, hasOpened, open, state]);

  const { body } = document;

  if (!body || state === "exited") return null;

  return createPortal(
    <div
      className={classNames(className, {
        "bractlet-modal": true,
        "bractlet-modal--open": state === "entered",
      })}
      data-cy={dataCy}
      data-testid="modal"
    >
      <div
        aria-hidden="true"
        className="bractlet-modal--backdrop"
        onClick={(e) => {
          if (backdropCancels && state === "entered") {
            _setOpen(false);
            onCancel?.(e);
          }
        }}
      />
      <div
        className="bractlet-modal--card"
        style={minWidth ? { minWidth } : undefined}
      >
        <div
          className={classNames({
            "bractlet-modal--header": true,
            "bractlet-modal--header--empty": !title,
            "bractlet-modal--header--delete":
              (includeDeleteButton && !warn && !title) || errorState,
            "bractlet-modal--header--warn": warn,
          })}
        >
          {title}
          {includeTopRightClose && onClose && (
            <Icons.Close
              className="bractlet-modal--header-close-icon"
              onClick={onClose}
            />
          )}
        </div>
        <div className="bractlet-modal--body">{children}</div>
        {(props.buttons ||
          includeCancelButton ||
          includeSaveButton ||
          includeDeleteButton) &&
          // Render buttons component if any buttons are included, otherwise, dont
          buttons}
      </div>
    </div>,
    body
  );
};

const Modal = (
  allProps: Props & {
    as?: any;
  }
) => {
  const { as, ...props } = allProps;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = props.open === undefined ? internalOpen : props.open;
  const modalContextValue = React.useMemo(
    () => ({
      _open: internalOpen,
      _setOpen: setInternalOpen,
    }),
    [internalOpen]
  );

  const [transitionState, toggle] = useTransitionState({
    timeout: 250,
    preEnter: true,
    initialEntered: open,
  });

  React.useEffect(() => {
    toggle(open);
  }, [open, toggle]);

  return (
    <>
      {as &&
        React.cloneElement(as, {
          onClick: (e) => {
            setInternalOpen(true);
            props.onOpen?.();
            e.stopPropagation();
          },
        })}
      <ModalContext.Provider value={modalContextValue}>
        <UnwrappedModal
          {...props}
          state={transitionState.status}
          open={open}
          _setOpen={setInternalOpen}
        />
      </ModalContext.Provider>
    </>
  );
};

const ModalContext = React.createContext<{
  _open: boolean;
  _setOpen: any;
}>({
  _open: false,
  _setOpen: () => {},
});

const ModalCloseButton = (props: any) => {
  const { _setOpen } = React.useContext(ModalContext);
  return (
    <Button outline onClick={() => _setOpen(false)} {...props}>
      Cancel
    </Button>
  );
};

Modal.Close = ModalCloseButton;

export default Modal;
